import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import { lazyEnsureAllAdditionalAccountInfoData } from 'owa-account-source-list';
import { PerformanceDatapoint } from 'owa-analytics';
import { isFolderInFavorites, loadFavorites } from 'owa-favorites';
import { FolderForestNodeType } from 'owa-favorites-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import folderStore from 'owa-folders';
import type { MailFolder } from 'owa-graph-schema';
import {
    lazyLoadGroupAccountSourcesForCurrentSession,
    lazyLoadGroups,
    lazyLoadGroupsFromSessionData,
} from 'owa-group-left-nav-actions';
import {
    lazyLoadUnifiedGroupsSettings,
    lazyUpdateFolderRulePermissionAllGroup,
} from 'owa-groups-shared-actions';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { loadMailFolders } from 'owa-mail-folder-store';
import { markFunction } from 'owa-performance';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { selectDefaultFolder } from '../index';

interface SelectedFolderData {
    isValid: boolean;
    folder: MailFolder;
}

/**
 * Load the folder forest to stores, i.e. folders and favorites
 */

export default markFunction(async function loadFolderForest(
    sessionData?: SessionData,
    apolloClientPromise?: Promise<ApolloClient<NormalizedCacheObject>>
) {
    const datapoint = new PerformanceDatapoint('loadFolderForest');

    await loadMailFolders(sessionData);

    lazyEnsureAllAdditionalAccountInfoData.importAndExecute(true /** forceUpdate */);

    loadFavorites();

    // If the selected folder is invalid or the selected folder is a favorites and the tree is expanded after we loaded favorites
    const selectedFolderdata = getSelectedNodeData();
    if (
        !selectedFolderdata.isValid ||
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2532 (50,11): Object is possibly 'undefined'.
        // @ts-expect-error
        (!getUserConfiguration().UserOptions.IsFavoritesFolderTreeCollapsed &&
            isFolderInFavorites(selectedFolderdata?.folder?.id))
    ) {
        selectDefaultFolder('ResetInbox', sessionData, apolloClientPromise);
    }

    if (isGroupsEnabled()) {
        if (isFeatureEnabled('acct-sourceIdAwareGroupsStore')) {
            datapoint.addCustomData({ isSourceIdAwareGroupStore: true });

            await lazyLoadGroupAccountSourcesForCurrentSession.importAndExecute();

            if (isFeatureEnabled('grp-loadFolders')) {
                lazyLoadUnifiedGroupsSettings.importAndExecute().then(() => {
                    lazyUpdateFolderRulePermissionAllGroup.importAndExecute();
                });
            } else {
                lazyLoadUnifiedGroupsSettings.importAndExecute();
            }
        } else {
            lazyLoadGroupsFromSessionData.importAndExecute();

            if (isFeatureEnabled('grp-loadFolders')) {
                Promise.all([
                    lazyLoadGroups.importAndExecute(true),
                    lazyLoadUnifiedGroupsSettings.importAndExecute(),
                ]).then(() => {
                    lazyUpdateFolderRulePermissionAllGroup.importAndExecute();
                });
            } else {
                lazyLoadGroups.importAndExecute(true);
                lazyLoadUnifiedGroupsSettings.importAndExecute();
            }
        }
    }

    datapoint.end();
},
'ff');

function getSelectedNodeData(): SelectedFolderData {
    const selectedNode = getSelectedNode();

    let data: SelectedFolderData = {
        isValid: false,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (72,9): Type 'null' is not assignable to type 'MailFolder'.
        // @ts-expect-error
        folder: null,
    };
    switch (selectedNode.type) {
        case FolderForestNodeType.Folder:
        case FolderForestNodeType.Persona:
        case FolderForestNodeType.PrivateDistributionList:
            const selectedFolderId = selectedNode.id;
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2322 (82,19): Type 'MailFolder | null | undefined' is not assignable to type 'MailFolder'.
            // @ts-expect-error
            const folder: MailFolder = selectedFolderId
                ? folderStore.folderTable.get(selectedFolderId)
                : null;
            data.isValid = folder != null;
            data.folder = folder;
            break;

        case FolderForestNodeType.Group:
            data.isValid = true;
            break;

        default:
            break;
    }

    return data;
}
